import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';
import { isValid, getErrors, useValidation } from '@totalsoft/pure-validations-react';
import { IconButton, TextField } from '@totalsoft/rocket-ui';
import { TableCell, TableRow } from '@mui/material';
import { emptyString } from 'utils/constants';
import { useTranslation } from 'react-i18next';
import { textResourceValidator } from '../validators';
import { useSubscription } from 'hooks/apollo';
import { TEXT_RESOURCE_CHANGED } from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import FlexBox from 'components/common/FlexBox';

const TextResourceListItem = ({ data, onSave, onDelete, pendingManagersRef }) => {
  const [t] = useTranslation();
  const addToast = useToast();

  const [editing, setEditing] = useState();
  const [pending, setPending] = useState();

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(data);
  const [validation, validate] = useValidation(textResourceValidator);

  useEffect(() => {
    resetLens(data);
  }, [data, resetLens]);

  useEffect(() => {
    const removeExistingManager = () => {
      if (pendingManagersRef.current.some(x => x.id === data.id))
        pendingManagersRef.current = pendingManagersRef.current.filter(x => x.id !== data.id);
    };

    removeExistingManager();

    pendingManagersRef.current.push({ id: data.id, handler: () => setPending(false) });

    return () => {
      removeExistingManager();
    };
  }, [data, pendingManagersRef]);

  // #region Handlers

  const toggleEditing = useCallback(() => {
    setEditing(prev => {
      if (prev) resetLens(data);
      return !prev;
    });
  }, [resetLens, data]);

  const handleSave = () => {
    const input = lens |> get;
    if (!validate(input)) return;
    setPending(true);
    onSave(input);
  };

  const handleDelete = useCallback(() => {
    setPending(true);
    onDelete(data.id);
  }, [data, onDelete]);

  // #endregion

  const dirty = useMemo(() => isDirty(dirtyInfo), [dirtyInfo]);

  useSubscription(TEXT_RESOURCE_CHANGED, {
    variables: { id: data.id },
    onData: ({ client }) => {
      addToast(t('TextResource.Modified'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedTextResources' });
      client.cache.gc();
      resetLens();
      setPending(false);
      setEditing(false);
    }
  });

  return (
    <>
      <TableRow>
        <TableCell>
          <TextField
            fullWidth
            id='text-resource-code'
            value={(lens.textResourceCode |> get) || emptyString}
            onChange={lens.textResourceCode |> set}
            error={!isValid(validation.textResourceCode)}
            helperText={getErrors(validation.textResourceCode)}
            disabled={!editing || pending}
          />
        </TableCell>
        <TableCell>
          <TextField
            fullWidth
            id='text-resource-label'
            value={(lens.label |> get) || emptyString}
            onChange={lens.label |> set}
            error={!isValid(validation.label)}
            helperText={getErrors(validation.label)}
            disabled={!editing || pending}
          />
        </TableCell>
        <TableCell>
          <TextField
            fullWidth
            id='text-resource-labelENU'
            value={(lens.labelENU |> get) || emptyString}
            onChange={lens.labelENU |> set}
            disabled={!editing || pending}
          />
        </TableCell>
        <TableCell>
          <TextField
            fullWidth
            id='text-resource-labelFRA'
            value={(lens.labelFRA |> get) || emptyString}
            onChange={lens.labelFRA |> set}
            disabled={!editing || pending}
          />
        </TableCell>
        <TableCell>
          <FlexBox gap={0.5} justifyContent='end'>
            {!editing && (
              <IconButton
                type='edit'
                size='small'
                color='primary'
                variant='text'
                tooltip={t('BehaviorParamTypes.Edit')}
                onClick={toggleEditing}
                disabled={pending}
              />
            )}
            {editing && (
              <>
                <IconButton
                  type='cancel'
                  title={t('BehaviorParamTypes.Cancel')}
                  size={'small'}
                  color='primary'
                  variant='text'
                  onClick={toggleEditing}
                  disabled={pending}
                />
                <IconButton
                  type='save'
                  size={'small'}
                  color='primary'
                  variant='text'
                  title={t('BehaviorParamTypes.Save')}
                  onClick={handleSave}
                  disabled={!dirty || pending}
                />
              </>
            )}
            <IconButton
              type='delete'
              title={t('BehaviorParamTypes.Delete')}
              size={'small'}
              color='primary'
              variant='text'
              onClick={handleDelete}
              disabled={pending}
            />
          </FlexBox>
        </TableCell>
      </TableRow>
    </>
  );
};

TextResourceListItem.propTypes = {
  data: PropTypes.object,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  pendingManagersRef: PropTypes.object
};

export default TextResourceListItem;
